// Tokens live in localStorage (shared across tabs on the same origin) so a user who's logged
// into Puzzel in one tab is recognised as logged in in every other open tab too, and logging out
// propagates everywhere via the "storage" event (see PuzzelAuthenticationContext.tsx).
const ACCESS_TOKEN_KEY = "PUZZEL_ACCESS_TOKEN";
const REFRESH_TOKEN_KEY = "PUZZEL_REFRESH_TOKEN";
const EXPIRES_AT_KEY = "PUZZEL_TOKEN_EXPIRES_AT";
const CUSTOMER_KEY_KEY = "PUZZEL_CUSTOMER_KEY";
const PUZZEL_USER_ID_KEY = "PUZZEL_USER_ID";
const PUZZEL_USERNAME_KEY = "PUZZEL_USERNAME";

// Custom claims on the access token identifying the org/agent - used to build the CC5 API URL.
const CUSTOMER_KEY_CLAIM = "urn:puzzel:cc:slid";
const PUZZEL_USER_ID_CLAIM = "urn:puzzel:cc:sluid";
const PUZZEL_USERNAME_CLAIM = "urn:puzzel:cc:sluname";

// Treat a token as expired slightly early to avoid racing a request against the real expiry.
const EXPIRY_LEEWAY_MS = 30_000;

// Best-effort - Puzzel may issue opaque tokens instead of JWTs, in which case this returns null.
export const tryDecodeJwtClaims = (
  token: string,
): Record<string, unknown> | null => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    // atob gives a Latin-1 string; re-encode via percent-escapes so multi-byte UTF-8 (æøå) survives.
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((byte) => `%${byte.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export type PuzzelTokens = {
  accessToken: string;
  refreshToken?: string;
  expiresInSeconds: number;
};

export const savePuzzelTokens = ({
  accessToken,
  refreshToken,
  expiresInSeconds,
}: PuzzelTokens): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(
    EXPIRES_AT_KEY,
    String(Date.now() + expiresInSeconds * 1000),
  );
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  const claims = tryDecodeJwtClaims(accessToken);
  const customerKey = claims?.[CUSTOMER_KEY_CLAIM];
  if (typeof customerKey === "string") {
    localStorage.setItem(CUSTOMER_KEY_KEY, customerKey);
  }
  const puzzelUserId = claims?.[PUZZEL_USER_ID_CLAIM];
  if (typeof puzzelUserId === "string") {
    localStorage.setItem(PUZZEL_USER_ID_KEY, puzzelUserId);
  }
  const puzzelUsername = claims?.[PUZZEL_USERNAME_CLAIM];
  if (typeof puzzelUsername === "string") {
    localStorage.setItem(PUZZEL_USERNAME_KEY, puzzelUsername);
  }
};

export const clearPuzzelTokens = (): void => {
  [
    ACCESS_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    EXPIRES_AT_KEY,
    CUSTOMER_KEY_KEY,
    PUZZEL_USER_ID_KEY,
    PUZZEL_USERNAME_KEY,
  ].forEach((key) => localStorage.removeItem(key));
};

export const getValidPuzzelAccessToken = (): string | null => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiresAt = Number(localStorage.getItem(EXPIRES_AT_KEY));

  if (!accessToken || !expiresAt || Date.now() > expiresAt - EXPIRY_LEEWAY_MS) {
    return null;
  }
  return accessToken;
};

export const getPuzzelRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

// Derived from the access token's own claims, not a hardcoded/configured value.
export const getPuzzelCustomerKey = (): string | null =>
  localStorage.getItem(CUSTOMER_KEY_KEY);

export const getPuzzelUserId = (): string | null =>
  localStorage.getItem(PUZZEL_USER_ID_KEY);

export const getPuzzelUsername = (): string | null =>
  localStorage.getItem(PUZZEL_USERNAME_KEY);

// Guards against infinite redirect loops when a fresh token still gets 401'd (e.g. wrong scope).
const LOGIN_ATTEMPTS_KEY = "PUZZEL_LOGIN_ATTEMPTS";
const LOGIN_ATTEMPTS_WINDOW_START_KEY = "PUZZEL_LOGIN_ATTEMPTS_WINDOW_START";
const MAX_AUTO_LOGIN_ATTEMPTS = 2;
const ATTEMPTS_WINDOW_MS = 60_000;

// Returns false once the attempt budget is used up, meaning the caller should stop retrying.
export const registerPuzzelLoginAttempt = (): boolean => {
  const windowStart = Number(
    sessionStorage.getItem(LOGIN_ATTEMPTS_WINDOW_START_KEY) ?? "0",
  );
  const windowExpired =
    !windowStart || Date.now() - windowStart > ATTEMPTS_WINDOW_MS;

  if (windowExpired) {
    sessionStorage.setItem(LOGIN_ATTEMPTS_WINDOW_START_KEY, String(Date.now()));
    sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, "1");
    return true;
  }

  // Slide the window forward each attempt, so it expires after 60s of inactivity, not 60s total.
  sessionStorage.setItem(LOGIN_ATTEMPTS_WINDOW_START_KEY, String(Date.now()));

  const attempts =
    Number(sessionStorage.getItem(LOGIN_ATTEMPTS_KEY) ?? "0") + 1;
  sessionStorage.setItem(LOGIN_ATTEMPTS_KEY, String(attempts));
  return attempts <= MAX_AUTO_LOGIN_ATTEMPTS;
};

// Only call once an API call actually succeeds - proves the token is valid, not just present.
export const resetPuzzelLoginAttempts = (): void => {
  sessionStorage.removeItem(LOGIN_ATTEMPTS_KEY);
  sessionStorage.removeItem(LOGIN_ATTEMPTS_WINDOW_START_KEY);
};

// Flags that the callback failed (e.g. token exchange rejected a disabled/misconfigured OIDC
// client), so the login button can tell the user instead of silently landing back on "Logg inn".
const LOGIN_ERROR_KEY = "PUZZEL_LOGIN_ERROR";

export const setPuzzelLoginError = (): void => {
  sessionStorage.setItem(LOGIN_ERROR_KEY, "1");
};

// Reads and clears in one go, so the message only shows once per failed attempt.
export const consumePuzzelLoginError = (): boolean => {
  const hadError = sessionStorage.getItem(LOGIN_ERROR_KEY) !== null;
  sessionStorage.removeItem(LOGIN_ERROR_KEY);
  return hadError;
};
